import { apiGet, apiPatch, apiPost } from "../fetch";
// JobAdApplicationRequirement is the ad's screening gate: a written cover
// letter, or answers to every question on the ad. Chosen per assignment.
import type { JobAdApplicationRequirement } from "../generated/django-enums";
import { getGlobalHeadersAsObject } from "../global-headers";

/** One language's wording of a screening question. */
export interface JobAdQuestionContent {
	language_code: string;
	text: string;
}

export interface JobAdQuestion {
	/** Absent on a question that has not been saved yet. */
	id?: number;
	text: string;
	position: number;
	/** The same question in the ad's other languages; `text` is the primary. */
	contents?: JobAdQuestionContent[];
}

/** One language's wording of the ad. The primary language lives on the ad's
 *  own fields; this is how every language is edited side by side. */
export interface JobAdContent {
	language_code: string;
	public_title: string;
	public_description: string;
	teaser: string;
}

export interface JobAd {
	id: number;
	assignment: number;
	slug: string;
	public_title: string;
	public_description: string;
	location_text: string;
	salary_min: number | null;
	salary_max: number | null;
	salary_currency: string;
	salary_period: string;
	employment_type: string;
	application_requirement: JobAdApplicationRequirement;
	questions: JobAdQuestion[];
	show_company: boolean;
	is_published: boolean;
	published_at: string | null;
	llm_prompt_template: number | null;
	public_url: string;
	/** Client company, ungated — the wizard preview follows the live
	 *  show_company toggle rather than the saved value. */
	client_company_name: string;
	agency_name: string;
	skills: string[];
	applications_count: number;
	/** The language the ad's own fields are written in. */
	primary_language: string;
	contents: JobAdContent[];
	/** Every language the ad is published in, primary first. */
	languages: string[];
	/** What the role's own language requirements and locations suggest. */
	suggested_languages: string[];
	/** Platsbanken's codes for the ad (core.JobTechConcept ids), by name too. */
	platsbanken_occupation: number | null;
	platsbanken_occupation_label: string;
	platsbanken_municipality: number | null;
	platsbanken_municipality_label: string;
	postal_code: string;
	/** Where the ad stands on Platsbanken; written by the sync task only. */
	platsbanken_id: string;
	platsbanken_status:
		| "NOT_SENT"
		| "LISTED"
		| "UNPUBLISHED"
		| "REFUSED"
		| "NO_CONSENT";
	/** Why it is not listed: what is missing, or Platsbanken's refusal. */
	platsbanken_error: string;
	created: string;
	updated: string;
}

/** The look a customer's public pages and embeds carry, and where their
 *  embed was last loaded from. */
export interface JobAdBrand {
	font_stack: string;
	accent_color: string;
	accent_text_color: string;
	text_color: string;
	surface_color: string;
	background_color: string;
	/** The page on the customer's own site that carries the ad embed. */
	job_page_url: string;
	site_builder: string;
	/** Read-only: written by the embed itself when a visitor loads it. */
	last_embed_host: string;
	last_embed_seen_at: string | null;
}

/** One generated language of an ad. */
export interface JobAdDraftContent {
	language_code: string;
	public_title: string;
	public_description: string;
	teaser: string;
	location: string;
}

export interface JobAdDraft {
	/** The first language, flat — for a caller showing one draft. */
	title: string;
	body_markdown: string;
	location: string;
	languages: string[];
	contents: JobAdDraftContent[];
	prompt_template_id: number;
	/** Platsbanken's occupation for the role; null when none matched. */
	occupation: { id: number; label: string } | null;
}

export interface ScreeningAnswer {
	question: string;
	answer: string;
}

export interface JobApplicationRow {
	id: number;
	job_ad: number;
	applicant_name: string;
	email: string;
	phone: string;
	message: string;
	/** Empty unless the ad asked screening questions instead of a letter. */
	answers: ScreeningAnswer[];
	linkedin_url: string;
	cv_filename: string;
	status: number;
	status_display: string;
	person: number | null;
	responded_at: string | null;
	created: string;
}

export interface JobAdInput {
	assignment: number;
	/** Public address; blank lets the server derive it from the title. */
	slug?: string;
	public_title: string;
	public_description: string;
	location_text: string;
	salary_min: number | null;
	salary_max: number | null;
	salary_currency: string;
	salary_period: string;
	employment_type: string;
	/** Mandatory on create — the server refuses an ad that states no gate.
	 *  Omitted from a partial update, the stored value stands. */
	application_requirement: JobAdApplicationRequirement;
	questions?: JobAdQuestion[];
	/** Every language of the ad. The entry matching primary_language is
	 *  written to the ad's own fields, the rest to their own rows. */
	contents?: JobAdContent[];
	primary_language?: string;
	show_company: boolean;
	is_published: boolean;
	llm_prompt_template: number | null;
}

export async function getJobAdByAssignment(
	assignmentId: number
): Promise<JobAd | null> {
	// pagination_class = None → bare array.
	const ads = (await apiGet(
		`/api/v2/job-ads/?assignment=${assignmentId}`
	)) as JobAd[];
	return ads[0] ?? null;
}

/** Ads on a checkout without a jobs domain get a relative URL — resolve it
 * against the current host so links and the copy button are usable. */
export function absoluteAdUrl(url: string): string {
	return url.startsWith("/") ? `${window.location.origin}${url}` : url;
}

/** Fired after an ad is created or edited.
 *
 * The assignment carries its ad's url and published state (`job_ad`), so any
 * view listing that assignment is one step behind — showing no ad, or a draft
 * that has since gone live — until it refetches the row. */
export const JOB_AD_CHANGED_EVENT = "job-ad-changed";

export interface JobAdChangedDetail {
	assignmentId: number;
}

function announceJobAdChange(ad: JobAd): JobAd {
	window.dispatchEvent(
		new CustomEvent<JobAdChangedDetail>(JOB_AD_CHANGED_EVENT, {
			detail: { assignmentId: ad.assignment },
		})
	);
	return ad;
}

export async function createJobAd(payload: JobAdInput): Promise<JobAd> {
	return announceJobAdChange((await apiPost(`/api/v2/job-ads/`, payload)) as JobAd);
}

export async function updateJobAd(
	id: number,
	payload: Partial<JobAdInput>
): Promise<JobAd> {
	return announceJobAdChange(
		(await apiPatch(`/api/v2/job-ads/${id}/`, payload)) as JobAd
	);
}

/** Raw role fields — the creation wizard drafts an ad before the
 * Assignment exists, so it sends what is on screen instead of an id. */
export interface JobAdPreviewInput {
	title: string;
	summary?: string;
	description?: string;
	requirements?: { text: string; mandatory: boolean }[];
	company?: number | null;
	location_text?: string;
	/** The role's language filters (LCIDs) and search locations (ids): what
	 *  the server derives the ad's languages from when none are given. */
	language_filters?: number[];
	search_locations?: number[];
}

export async function generateJobAd(
	params: ({ assignment_id: number } | JobAdPreviewInput) & {
		show_company: boolean;
		hint?: string;
		regenerate?: boolean;
		/** Every language to write the ad in, in one call. Absent, the role's
		 *  own languages decide. */
		languages?: string[];
	}
): Promise<JobAdDraft> {
	return (await apiPost(`/api/v2/job-ads/generate/`, params)) as JobAdDraft;
}

/** Which role the questions are drafted for: a saved assignment, or the raw
 *  fields the creation wizard has on screen before the Assignment exists. */
export type JobAdQuestionSource = { assignment_id: number } | JobAdPreviewInput;

/** AI-draft the screening questions for a role, from its spec and ad body. */
export async function generateJobAdQuestions(
	params: JobAdQuestionSource & {
		public_description: string;
		hint?: string;
		regenerate?: boolean;
		/** Ask them in every language the ad is published in, in one call, so
		 *  the same question keeps its position in each. */
		languages?: string[];
	}
): Promise<{
	questions: string[];
	languages: string[];
	questions_by_language: Record<string, string[]>;
	prompt_template_id: number;
}> {
	return (await apiPost(`/api/v2/job-ads/generate_questions/`, params)) as {
		questions: string[];
		languages: string[];
		questions_by_language: Record<string, string[]>;
		prompt_template_id: number;
	};
}

/** The look this customer's public pages and embeds carry. */
export async function getJobAdBrand(): Promise<JobAdBrand> {
	return (await apiGet(`/api/v2/job-ads/brand/`)) as JobAdBrand;
}

export async function updateJobAdBrand(
	payload: Partial<JobAdBrand>
): Promise<JobAdBrand> {
	return (await apiPatch(`/api/v2/job-ads/brand/`, payload)) as JobAdBrand;
}

/** The two emails every applicant to a role gets once graded. */
export type ApplicantReplyOutcome = "accept" | "reject";

/** One language of one reply — a TemplateContent row, edited in place. */
export interface ApplicantReplyContent {
	id: number;
	language_code: string;
	subject: string;
	template: string;
}

export type ApplicantReplies = Record<ApplicantReplyOutcome, ApplicantReplyContent[]>;

/** Both replies of a role, every language. Keyed by assignment: the replies
 *  hang off the role, so they exist before the ad row does. */
export async function getApplicantReplies(
	assignmentId: number
): Promise<ApplicantReplies> {
	return (await apiGet(
		`/api/v2/job-ads/applicant_replies/?assignment=${assignmentId}`
	)) as ApplicantReplies;
}

/** AI-draft and store both replies — the role's answers from this moment. */
export async function generateApplicantReplies(params: {
	assignment_id: number;
	hint?: string;
	regenerate?: boolean;
}): Promise<{ replies: ApplicantReplies; prompt_template_id: number }> {
	return (await apiPost(`/api/v2/job-ads/generate_replies/`, params)) as {
		replies: ApplicantReplies;
		prompt_template_id: number;
	};
}

/** One language of one reply as drafted — not stored yet, so no row id. */
export interface ApplicantReplyDraft {
	subject: string;
	body: string;
}

/** Both replies, every language: what the creation wizard edits before the
 *  role exists and hands to the Jobylon link on save. */
export type ApplicantReplyDrafts = Record<
	ApplicantReplyOutcome,
	Record<string, ApplicantReplyDraft>
>;

/** AI-draft both replies for a role that is not saved yet. Stores nothing —
 *  the drafts are stored by `linkJobylonJobToAssignment` when the role saves. */
export async function previewApplicantReplies(params: {
	role_title: string;
	role_description: string;
	/** Every language to write in; absent, English. */
	target_languages?: string[];
	hint?: string;
	regenerate?: boolean;
}): Promise<ApplicantReplyDrafts> {
	return (await apiPost(
		`/api/v2/job-ads/preview_replies/`,
		params
	)) as ApplicantReplyDrafts;
}

/** One reply as it would be emailed to one applicant, signed by the caller. */
export interface ApplicantReplyPreview {
	subject: string;
	body: string;
	template_id: number;
	language_code: string;
}

/** Null when the role has no such reply yet (the server answers 200 + {}). */
export async function getApplicantReplyPreview(
	assignmentId: number,
	personId: number,
	outcome: ApplicantReplyOutcome
): Promise<ApplicantReplyPreview | null> {
	const params = new URLSearchParams({
		assignment: String(assignmentId),
		person: String(personId),
		outcome,
	});
	const preview = (await apiGet(
		`/api/v2/job-ads/applicant_reply_preview/?${params.toString()}`
	)) as Partial<ApplicantReplyPreview>;
	return preview.template_id === undefined
		? null
		: (preview as ApplicantReplyPreview);
}

export async function getJobAdApplications(id: number): Promise<JobApplicationRow[]> {
	return (await apiGet(`/api/v2/job-ads/${id}/applications/`)) as JobApplicationRow[];
}

export interface JobAdsBase {
	/** Host without the slug — the slug only exists once the ad is saved. */
	base_url: string;
	/** Null when the group never claimed a careers address. */
	tenant: string | null;
	/** "" on dev checkouts that run without a jobs domain. */
	domain: string;
	group_id: number;
	/** The group's own name, which the claim prompt slugifies as its default. */
	group_name: string;
}

export async function getJobAdsBase(): Promise<JobAdsBase> {
	return (await apiGet(`/api/v2/job-ads/base_url/`)) as JobAdsBase;
}

/** Claim the group's careers address (also its hosted-email subdomain).
 * The server refuses a second change — it is set once, permanently. */
export async function claimJobAdsTenant(
	groupId: number,
	tenant: string
): Promise<void> {
	await apiPatch(`/api/v2/user_profile_group/${groupId}/`, {
		email_subdomain: tenant,
	});
}

export interface SlugCheck {
	slug: string;
	available: boolean;
	/** A free alternative when the wanted one is taken. */
	suggestion: string;
}

export async function checkJobAdSlug(
	slug: string,
	excludeId?: number
): Promise<SlugCheck> {
	const params = new URLSearchParams({ slug });
	if (excludeId !== undefined) params.set("exclude", String(excludeId));
	return (await apiGet(
		`/api/v2/job-ads/slug_available/?${params.toString()}`
	)) as SlugCheck;
}

/**
 * Put a picture or clip from the recruiter's machine into the ad body.
 *
 * XMLHttpRequest rather than fetch, and the only place in the app that reaches
 * for it: fetch reports no upload progress, and a 200 MB clip with no progress
 * bar is indistinguishable from a frozen editor. Resolves to the permanent
 * address the markdown carries.
 */
export function uploadJobAdMedia(
	file: File,
	onProgress: (fraction: number) => void
): Promise<string> {
	return new Promise((resolve, reject) => {
		const body = new FormData();
		body.append("file", file);
		const request = new XMLHttpRequest();
		request.open("POST", "/api/v2/job-ads/upload_media/");
		for (const [header, value] of Object.entries(getGlobalHeadersAsObject())) {
			request.setRequestHeader(header, value);
		}
		request.upload.addEventListener("progress", (event) => {
			if (event.lengthComputable) onProgress(event.loaded / event.total);
		});
		request.addEventListener("load", () => {
			let payload: { url?: string; error?: string } = {};
			try {
				payload = JSON.parse(request.responseText) as typeof payload;
			} catch {
				// A proxy timing out mid-upload answers HTML, not JSON. The
				// status is still the honest thing to report.
			}
			if (request.status === 201 && payload.url) {
				resolve(payload.url);
				return;
			}
			reject(new Error(payload.error ?? `Upload failed (${request.status})`));
		});
		request.addEventListener("error", () =>
			reject(new Error("Upload failed — the connection dropped"))
		);
		request.addEventListener("abort", () => reject(new Error("Upload cancelled")));
		request.send(body);
	});
}
